<script setup lang="ts">
import { nextTick, useTemplateRef } from 'vue';
import TooltipComponent, { type TooltipData } from '../../tooltip/index.vue';
import type { ScaleBand, ScaleLinear } from 'd3';

// ================================================ Composables
const { getTailwindColor } = useTailwind();

// ================================================ Props
const emit = defineEmits<{
  (e: 'hover-point', index: number): void;
  (e: 'unhover-point'): void;
}>();

const props = defineProps<{
  keys: string[];
  loading: boolean;
  yScale: ScaleLinear<number, number, never>;
  xScale: ScaleBand<string>;
  tooltip?: (dataIndex: number) => TooltipData | undefined;
}>();

// ================================================ Data
const isHovered = ref(false);
const tooltipData = ref<TooltipData | null>(null);
const tooltipPosition = ref({
  show: false,
  dataIndex: 0,
  x: 0,
  y: 0,
  arrowPosition: 'left' as 'top' | 'right' | 'bottom' | 'left',
});

const overlayRef = useTemplateRef<HTMLElement>('overlayRef');
const tooltipRef = useTemplateRef<HTMLElement>('tooltipRef');
const tooltipContainerRef = useTemplateRef<HTMLElement>('tooltipContainerRef');
const verticalLineRef = useTemplateRef<SVGLineElement>('verticalLineRef');

// ================================================ Methods
const showTooltip = (event: MouseEvent) => {
  if (!props.tooltip || !overlayRef.value || !tooltipContainerRef.value) return;

  const rect = overlayRef.value.getBoundingClientRect();
  const relativeX = event.clientX - rect.left;
  const relativePercentX = (relativeX / rect.width) * 100;

  // Find the closest data point
  let closestIndex = 0;
  let minDistance = Infinity;

  props.keys.forEach((key, index) => {
    const keyPosition = (props.xScale(key) ?? 0) + props.xScale.bandwidth() / 2;
    const distance = Math.abs(keyPosition - relativePercentX);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  // Get tooltip data
  tooltipData.value = props.tooltip(closestIndex) ?? null;
  if (!tooltipData.value) return;

  // Calculate the exact position for the vertical line
  const closestKey = props.keys[closestIndex];
  if (!closestKey) return;

  const lineXPosition = (props.xScale(closestKey) ?? 0) + props.xScale.bandwidth() / 2;

  // Show vertical line at the exact data point position
  if (verticalLineRef.value) {
    verticalLineRef.value.style.display = 'block';
    verticalLineRef.value.setAttribute('x1', lineXPosition.toString());
    verticalLineRef.value.setAttribute('x2', lineXPosition.toString());
  }

  // Calculate tooltip position
  const containerRect = tooltipContainerRef.value.getBoundingClientRect();
  const chartRect = overlayRef.value.getBoundingClientRect();

  // Fixed vertical position - top of the chart
  const fixedY = chartRect.top;
  const relativeY = fixedY - containerRect.top;

  // Determine if we're in the left or right half of the chart
  const isLeftHalf = relativePercentX < 50;

  // Initial position approximation
  let initialPosX = isLeftHalf ? (lineXPosition / 100) * chartRect.width + 10 : (lineXPosition / 100) * chartRect.width - 200 - 10; // 200 is approximate tooltip width

  // Ensure position stays within chart bounds
  if (initialPosX < 0) {
    initialPosX = 0;
  } else if (initialPosX + 200 > chartRect.width) {
    initialPosX = chartRect.width - 200;
  }

  // Set initial tooltip position
  tooltipPosition.value = {
    show: true,
    dataIndex: closestIndex,
    x: initialPosX + chartRect.left - containerRect.left,
    y: relativeY,
    arrowPosition: isLeftHalf ? 'left' : 'right',
  };

  // Wait for tooltip to render and get actual width
  nextTick(() => {
    if (!tooltipRef.value) return;

    const tooltipElement = tooltipRef.value as any;
    const actualTooltipWidth = tooltipElement.$el?.offsetWidth ?? 200;

    // Recalculate position with actual width
    let finalPosX;
    if (isLeftHalf) {
      finalPosX = (lineXPosition / 100) * chartRect.width + 10;
    } else {
      finalPosX = (lineXPosition / 100) * chartRect.width - actualTooltipWidth - 10;
    }

    // Ensure final position stays within bounds
    if (finalPosX < 0) {
      finalPosX = 0;
    } else if (finalPosX + actualTooltipWidth > chartRect.width) {
      finalPosX = chartRect.width - actualTooltipWidth;
    }

    // Update with final position
    tooltipPosition.value = {
      ...tooltipPosition.value,
      x: finalPosX + chartRect.left - containerRect.left,
    };
  });

  emit('hover-point', closestIndex);
};

const hideTooltip = () => {
  tooltipPosition.value.show = false;
  tooltipData.value = null;

  if (verticalLineRef.value) {
    verticalLineRef.value.style.display = 'none';
  }

  emit('unhover-point');
};

const handleMouseMove = (event: MouseEvent) => {
  if (!isHovered.value) return;
  showTooltip(event);
};

const handleMouseEnter = () => {
  isHovered.value = true;
};

const handleMouseLeave = () => {
  isHovered.value = false;
  hideTooltip();
};
</script>

<template>
  <div>
    <!-- Invisible overlay for mouse events -->
    <div
      ref="overlayRef"
      class="absolute inset-0 w-full h-full z-[25]"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @mousemove="handleMouseMove"
    />

    <!-- Vertical line indicator -->
    <svg
      class="absolute inset-0 h-full w-full overflow-visible z-[21] pointer-events-none"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      <line
        ref="verticalLineRef"
        y1="0"
        y2="100"
        :stroke="getTailwindColor('gray-400')"
        stroke-width="0.5"
        stroke-dasharray="2,2"
        vector-effect="non-scaling-stroke"
        style="display: none"
      />
    </svg>

    <!-- Tooltip container -->
    <div ref="tooltipContainerRef" class="absolute top-0 left-0 w-full h-full pointer-events-none z-[30]">
      <TooltipComponent
        v-if="tooltipData && tooltipPosition.show"
        ref="tooltipRef"
        class="absolute pointer-events-none"
        :style="{
          left: tooltipPosition.x + 'px',
          top: tooltipPosition.y + 'px',
        }"
        :title="tooltipData.title"
        :subtitle="tooltipData.subtitle"
        :body="tooltipData.body"
        :footer="tooltipData.footer"
        :description="tooltipData.description"
        :arrow-position="tooltipPosition.arrowPosition"
        :is-below="false"
      />
    </div>
  </div>
</template>
